import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArchiveRestore,
  ArrowLeftRight,
  ChevronDown,
  Info,
  Landmark,
  MoreVertical,
  Pencil,
  Plus,
  Scale,
  type LucideIcon,
} from 'lucide-react';
import { AnimatePresence, m } from 'motion/react';
import {
  ACCOUNT_TYPE_LABELS,
  type Account,
  type AccountsResponse,
  type AccountType,
} from '@lira/shared';
import { apiPatch, apiGet } from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';
import { cn } from '@/lib/cn';
import { useMonthStore } from '@/stores/month';
import { useComposeStore } from '@/stores/compose';
import { toast } from '@/stores/toast';
import { CategoryIcon, EmptyState, MoneyText, Skeleton } from '@/components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { ACCOUNT_TYPE_ICONS } from './accountMeta';
import { AccountFormModal } from './AccountFormModal';
import { BalanceAdjustModal } from './BalanceAdjustModal';

const EASE = [0.16, 1, 0.3, 1] as const;

interface AccountSection {
  key: string;
  label: string;
  icon: LucideIcon;
  accounts: Account[];
  subtotalCents: number;
}

/** Conta corrente + Dinheiro formam "Contas principais"; os demais tipos ficam
 *  cada um no seu grupo, nesta ordem. */
const MAIN_TYPES: AccountType[] = ['checking', 'cash'];
const SECONDARY_ORDER: AccountType[] = ['savings', 'investment', 'voucher', 'other'];

function subtotalOf(list: Account[]): number {
  return list.reduce((sum, a) => sum + a.currentBalanceCents, 0);
}

function groupAccounts(accounts: Account[]): AccountSection[] {
  const sorted = [...accounts].sort((a, b) => a.sortOrder - b.sortOrder);
  const sections: AccountSection[] = [];

  const main = sorted.filter((a) => MAIN_TYPES.includes(a.type));
  if (main.length > 0) {
    sections.push({ key: 'main', label: 'Contas principais', icon: Landmark, accounts: main, subtotalCents: subtotalOf(main) });
  }
  for (const type of SECONDARY_ORDER) {
    const list = sorted.filter((a) => a.type === type);
    if (list.length > 0) {
      sections.push({
        key: type,
        label: ACCOUNT_TYPE_LABELS[type],
        icon: ACCOUNT_TYPE_ICONS[type],
        accounts: list,
        subtotalCents: subtotalOf(list),
      });
    }
  }
  return sections;
}

/** Card tracejado "Nova conta" (primeiro item do primeiro grupo). */
function NewAccountCard({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-[11.5rem] flex-col items-center justify-center gap-3 rounded-[var(--radius-card)] border-2 border-dashed border-border bg-surface p-4 text-center transition-colors hover:border-primary/50 hover:bg-primary-weak/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <span className="grid size-11 place-items-center rounded-full bg-primary text-white shadow-fab">
        <Plus className="size-6" />
      </span>
      <span className="text-sm font-semibold text-primary">Nova conta</span>
    </button>
  );
}

function AccountMenu({
  account,
  onEdit,
  onAdjust,
  onTransactions,
}: {
  account: Account;
  onEdit: (a: Account) => void;
  onAdjust: (a: Account) => void;
  onTransactions: (a: Account) => void;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top?: number; bottom?: number; right: number }>({ right: 8 });

  const updatePosition = () => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const estimatedMenuHeight = 164;
    const opensAbove = rect.bottom + estimatedMenuHeight + 8 > window.innerHeight;
    setPosition({
      right: Math.max(8, window.innerWidth - rect.right),
      ...(opensAbove
        ? { bottom: Math.max(8, window.innerHeight - rect.top + 4) }
        : { top: rect.bottom + 4 }),
    });
  };

  useEffect(() => {
    if (!open) return;
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  // Só Escape aqui; o descarte por clique-fora usa um backdrop (abaixo). O card
  // é clicável (overlay), então um listener de pointerdown no document deixaria
  // o mesmo clique de descarte também navegar — o backdrop absorve esse clique.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const archive = useMutation({
    mutationFn: () =>
      apiPatch(`/accounts/${account.id}/archive`, { archived: !account.archived }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['accounts'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast(account.archived ? 'Conta desarquivada.' : 'Conta arquivada.', 'success');
    },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => {
          if (!open) updatePosition();
          setOpen((v) => !v);
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Ações da conta"
        className="grid size-11 -mr-1 -mt-1 place-items-center rounded-full text-muted transition-colors hover:bg-canvas hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <MoreVertical className="size-5" />
      </button>

      {/* O portal tira menu/backdrop do overflow animado da seção. O backdrop
          continua abaixo do menu e absorve o clique sem navegar pelo card. */}
      {createPortal(
        <>
          {open && (
            <div
              className="fixed inset-0 z-50"
              aria-hidden
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
              }}
            />
          )}

          <AnimatePresence>
            {open && (
              <m.div
                initial={{ opacity: 0, y: -6, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -6, scale: 0.98 }}
                transition={{ duration: 0.15, ease: EASE }}
                role="menu"
                style={{ ...position, backgroundColor: 'var(--color-surface)' }}
                className="fixed z-[60] min-w-52 overflow-hidden rounded-lg border border-border bg-surface! py-1 shadow-hover ring-1 ring-black/5 dark:ring-white/10"
              >
            <MenuItem
              icon={Pencil}
              label="Editar"
              onClick={() => {
                setOpen(false);
                onEdit(account);
              }}
            />
            <MenuItem
              icon={account.archived ? ArchiveRestore : Archive}
              label={account.archived ? 'Desarquivar' : 'Arquivar'}
              onClick={() => {
                setOpen(false);
                archive.mutate();
              }}
            />
            <MenuItem
              icon={ArrowLeftRight}
              label="Transações"
              onClick={() => {
                setOpen(false);
                onTransactions(account);
              }}
            />
            <MenuItem
              icon={Scale}
              label="Reajuste de saldo"
              onClick={() => {
                setOpen(false);
                onAdjust(account);
              }}
            />
              </m.div>
            )}
          </AnimatePresence>
        </>,
        document.body,
      )}
    </div>
  );
}

function MenuItem({
  icon: Icon,
  label,
  onClick,
}: {
  icon: typeof Pencil;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-ink transition-colors hover:bg-canvas"
    >
      <Icon className="size-4 shrink-0 text-muted" />
      <span className="flex-1">{label}</span>
    </button>
  );
}

function AccountCard({
  account,
  onEdit,
  onAdjust,
  onTransactions,
  onAddExpense,
}: {
  account: Account;
  onEdit: (a: Account) => void;
  onAdjust: (a: Account) => void;
  onTransactions: (a: Account) => void;
  onAddExpense: (a: Account) => void;
}) {
  // Card clicável (consistência "número leva à origem"): o corpo abre as
  // transações da conta. A camada-alvo cobre o card inteiro (z-0); o conteúdo é
  // pointer-events-none para deixar o clique cair na camada, e cada controle
  // (⋮, "Adicionar despesa") reativa os eventos com pointer-events-auto + z.
  return (
    <div className="group relative flex min-h-[11.5rem] flex-col rounded-[var(--radius-card)] bg-surface p-5 shadow-card transition-shadow hover:shadow-hover">
      <button
        type="button"
        onClick={() => onTransactions(account)}
        aria-label={`Ver transações de ${account.name}`}
        className="absolute inset-0 z-0 rounded-[var(--radius-card)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
      />

      <div className="pointer-events-none relative z-10 flex items-start gap-3">
        <CategoryIcon icon={account.icon} color={account.color} fallbackText={account.name} size={40} />
        <p className="min-w-0 flex-1 truncate pt-1 font-semibold text-ink">
          {account.name}
          {account.archived && (
            <span className="ml-2 rounded-full bg-canvas px-2 py-0.5 text-[11px] font-medium text-muted">
              Arquivada
            </span>
          )}
        </p>
        <div className="pointer-events-auto">
          <AccountMenu
            account={account}
            onEdit={onEdit}
            onAdjust={onAdjust}
            onTransactions={onTransactions}
          />
        </div>
      </div>

      <div className="pointer-events-none relative z-10 mt-3 space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-[13px] text-muted">Saldo atual</span>
          <MoneyText cents={account.currentBalanceCents} colorBySign bold />
        </div>
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1 text-[13px] text-muted">
            Saldo previsto
            <span
              title="Saldo atual + lançamentos pendentes do mês selecionado"
              className="pointer-events-auto inline-flex cursor-help"
            >
              <Info className="size-3.5 text-muted" aria-hidden />
            </span>
          </span>
          <MoneyText cents={account.predictedBalanceCents} colorBySign />
        </div>
      </div>

      <div className="pointer-events-none relative z-10 mt-auto border-t border-border pt-3">
        <button
          type="button"
          onClick={() => onAddExpense(account)}
          className="pointer-events-auto text-xs font-semibold uppercase tracking-wide text-primary transition-colors hover:text-primary-hover"
        >
          Adicionar despesa
        </button>
      </div>
    </div>
  );
}

export function AccountsPage() {
  const month = useMonthStore((s) => s.month);
  const navigate = useNavigate();
  const openCompose = useComposeStore((s) => s.openNew);
  const includeArchived = false;
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Account | undefined>(undefined);
  const [adjusting, setAdjusting] = useState<Account | null>(null);
  // Grupos recolhidos (dropdown). Começam todos abertos.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const toggleSection = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const query = useQuery({
    queryKey: ['accounts', month, includeArchived],
    queryFn: () =>
      apiGet<AccountsResponse>(`/accounts?month=${month}&includeArchived=${includeArchived}`),
  });

  const openNew = () => {
    setEditing(undefined);
    setFormOpen(true);
  };
  const openEdit = (account: Account) => {
    setEditing(account);
    setFormOpen(true);
  };
  const addExpense = (account: Account) => openCompose('expense', { accountId: account.id });
  const goToTransactions = (account: Account) =>
    navigate(`/transacoes?accountId=${account.id}`);

  const groups = query.data ? groupAccounts(query.data.accounts) : [];
  const isEmpty = query.isSuccess && groups.length === 0;

  return (
    <section className="space-y-7">
      <PageHeader
        title="Contas"
        subtitle="Saldos atuais e previstos das suas contas."
      />
      {/* A entrada de criação fica no primeiro card, como na referência. Não há
          totalizador/página extra entre a navegação e as seções de contas. */}

      {query.isLoading && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} variant="card" className="min-h-[8.75rem]" />
          ))}
        </div>
      )}

      {query.isError && (
        <p className="rounded-lg bg-expense-weak px-4 py-6 text-center text-sm text-expense">
          {errorMessage(query.error)}
        </p>
      )}

      {isEmpty && (
        <EmptyState
          icon={Landmark}
          title="Nenhuma conta ainda"
          description="Cadastre suas contas para acompanhar saldos e organizar seus lançamentos."
        >
          <button
            type="button"
            onClick={openNew}
            className="inline-flex h-11 items-center gap-2 rounded-full bg-primary px-6 text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:bg-primary-hover"
          >
            <Plus className="size-5" />
            Nova conta
          </button>
        </EmptyState>
      )}

      {groups.map((section, sectionIndex) => {
        const GroupIcon = section.icon;
        const isCollapsed = collapsed.has(section.key);
        return (
          <div key={section.key} className="space-y-3">
            {/* Cabeçalho = dropdown: clica p/ recolher/expandir o grupo. */}
            <button
              type="button"
              onClick={() => toggleSection(section.key)}
              aria-expanded={!isCollapsed}
              className="flex w-full items-center justify-between gap-3 rounded-lg py-1 text-left transition-opacity hover:opacity-80"
            >
              <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted">
                <ChevronDown
                  className={cn('size-4 text-muted transition-transform', isCollapsed && '-rotate-90')}
                />
                <GroupIcon className="size-4" />
                {section.label}
                <span className="rounded-full bg-canvas px-1.5 text-[11px] font-medium normal-case tabular-nums text-muted">
                  {section.accounts.length}
                </span>
              </h2>
              <MoneyText cents={section.subtotalCents} colorBySign bold className="text-sm" />
            </button>

            <AnimatePresence initial={false}>
              {!isCollapsed && (
                <m.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2, ease: EASE }}
                  className="overflow-hidden"
                >
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {sectionIndex === 0 && <NewAccountCard onClick={openNew} />}
                    {section.accounts.map((account) => (
                      <AccountCard
                        key={account.id}
                        account={account}
                        onEdit={openEdit}
                        onAdjust={setAdjusting}
                        onTransactions={goToTransactions}
                        onAddExpense={addExpense}
                      />
                    ))}
                  </div>
                </m.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}

      <AccountFormModal open={formOpen} onClose={() => setFormOpen(false)} account={editing} />
      <BalanceAdjustModal
        open={!!adjusting}
        onClose={() => setAdjusting(null)}
        account={adjusting}
      />
    </section>
  );
}
